import './FormikColorField.css';
import { ColorPicker } from '@ipfabric/ui-components/ColorPicker/ColorPicker';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useStableValue } from '@ipfabric/ui-components/hooks/useStableValue';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import type { StringKey } from '@ipfabric/utils';
import classNames from 'classnames';
import type { FormikProps } from 'formik';
import { type JSX, useEffect, useRef, useState } from 'react';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';
import { isAncestorElement } from 'utils/isAncestorElement';

const initialState: FieldState = {
  open: false,
  x: null,
  y: null,
};

interface Props<TFormikValues> {
  containerRef?: HTMLDivElement | null;
  wrapperClassName?: string;
  formik: FormikProps<TFormikValues>;
  name: StringKey<TFormikValues> | NestedFormikName;
}

interface FieldState {
  open: boolean;
  x: number | null;
  y: number | null;
}

export const FormikColorField = <TFormikValues,>(
  props: Props<TFormikValues>,
): JSX.Element => {
  const field = props.formik.getFieldProps(props.name);
  const fieldRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const dialogOpenDomEventListeners = useStableValue(
    () => new ManagedDomEventListeners(),
  );
  const [fieldState, setFieldState] = useState<FieldState>(initialState);
  const color = useRef<string>(field.value);

  const closeDialog = () => {
    field.onChange({
      target: {
        name: props.name,
        value: color.current,
      },
    });
    setFieldState(initialState);
  };

  const handleDocumentClick = useStableCallback((e: Event) => {
    const element = e.target as HTMLDivElement;
    if (fieldRef.current && !isAncestorElement(element, fieldRef.current)) {
      closeDialog();
      dialogOpenDomEventListeners.remove();
    }
  });

  const handleOpenClick = () => {
    if (!fieldState.open && fieldRef.current) {
      dialogOpenDomEventListeners.add([
        props.containerRef ?? document,
        'click',
        handleDocumentClick,
      ]);
      setFieldState({
        open: true,
        x: fieldRef.current.offsetLeft,
        y: fieldRef.current.offsetTop + fieldRef.current.offsetHeight,
      });
    } else {
      closeDialog();
    }
  };

  const handleChange = useStableCallback((colorResult: string) => {
    field.onChange({
      target: {
        name: props.name,
        value: colorResult,
      },
    });
    color.current = colorResult;
  });

  useEffect(() => {
    return () => {
      dialogOpenDomEventListeners.remove();
    };
  }, []);

  return (
    <div className={props.wrapperClassName} ref={fieldRef}>
      <span
        className='formik-color-field flex flex-row items-center'
        onClick={handleOpenClick}
      >
        <span
          className='formik-color-field__color ml-lgu-3'
          style={{ background: field.value }}
        />
        {field.value}
        <i
          className={classNames(
            'fa',
            fieldState.open ? 'fa-chevron-up' : 'fa-chevron-down',
            'text-lgu-base ml-lgu-3 mr-lgu-2',
          )}
        />
      </span>
      {fieldState.open && (
        <div
          className='formik-color-field__dialog'
          ref={dialogRef}
          style={{
            left: `${fieldState.x}px`,
            top: `${fieldState.y}px`,
          }}
        >
          <ColorPicker color={field.value} onColorChange={handleChange} />
        </div>
      )}
    </div>
  );
};
